import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { adminBootstrap, adminStopActingAs, useIsAdmin } from '../../lib/admin'
import { useIsMobile } from '../../lib/useMediaQuery'
import Icon from '../../components/Icon'
import type { IconName } from '../../components/Icon'
import Wordmark from '../../components/Wordmark'
import Splash from '../../components/Splash'
import AdminOverview from './AdminOverview'
import AdminUsers from './AdminUsers'
import AdminPlans from './AdminPlans'
import AdminDoctor from './AdminDoctor'
import AdminAudit from './AdminAudit'
import AdminProspects from './AdminProspects'

/**
 * The admin portal.
 *
 * Its own shell rather than a tab inside the app: the audience is one or two
 * people, the data is every account, and putting it behind the same nav as
 * "your tours" invites the wrong kind of accident.
 *
 * The guard below decides what renders. It is not the security boundary —
 * every call it makes re-checks the claim server-side, because a claim read in
 * a browser is a claim an attacker controls.
 */
type NavEntry = { to: string; label: string; icon: IconName; end?: boolean; superOnly?: boolean }

/** The entries this account can actually use. */
function navFor(isSuperAdmin: boolean): NavEntry[] {
  return NAV.filter((entry) => !entry.superOnly || isSuperAdmin)
}

const NAV: NavEntry[] = [
  { to: '/admin', label: 'Overview', icon: 'dashboard', end: true },
  { to: '/admin/users', label: 'Users', icon: 'people' },
  { to: '/admin/plans', label: 'Plans', icon: 'tickets' },

  { to: '/admin/doctor', label: 'Data doctor', icon: 'key' },
  { to: '/admin/audit', label: 'Audit log', icon: 'list' },
  // Handing out act-as grants is the higher tier's job, so the page that does
  // it isn't offered to anyone who couldn't use it.
  { to: '/admin/prospects', label: 'Prospects', icon: 'people', superOnly: true },
]

export default function Admin() {
  const { user } = useAuth()
  const { isAdmin, isSuperAdmin, checking, recheck } = useIsAdmin()
  const isMobile = useIsMobile()

  // The portal is noindex at the document level too — it should never be a
  // search result even if someone links to it.
  useEffect(() => {
    const previous = document.title
    document.title = 'Encore Admin'
    return () => {
      document.title = previous
    }
  }, [])

  const entries = navFor(isSuperAdmin)

  if (checking) return <Splash />

  if (!isAdmin) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', padding: 24 }}>
        <div className="panel panel-pad stack" style={{ maxWidth: 420, padding: 32, gap: 10 }}>
          <span className="t-eyebrow">Admin</span>
          <h1 className="t-display-s">Not available</h1>
          <p className="t-caption">
            This account doesn't have admin access. If it should, ask an existing admin to
            grant it — then sign out and back in so the change reaches your session.
          </p>
          <p className="t-spec-caption faint">Signed in as {user?.email}</p>
          <Bootstrap />
          <a className="btn btn-secondary" href="/app" style={{ alignSelf: 'flex-start' }}>
            Back to Encore
          </a>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: isMobile ? undefined : '220px minmax(0, 1fr)',
        minHeight: '100dvh',
        background: 'var(--bg)',
      }}
    >
      {isMobile ? <MobileNav entries={entries} /> : <DesktopNav entries={entries} email={user?.email ?? ''} />}

      <main style={{ minWidth: 0, overflow: 'auto' }}>
        <HeldGrant />
        {isAdmin && !isSuperAdmin && <ClaimSuperAdmin onGranted={() => void recheck()} />}
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="plans" element={<AdminPlans />} />
          <Route path="doctor" element={<AdminDoctor />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="prospects" element={<AdminProspects />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  )
}

/**
 * The one-time claim for the first admin.
 *
 * Shown to everyone, because hiding it would mean trusting the client to know
 * who's on the allowlist. The function refuses anyone not on it, and refuses
 * everyone once an admin exists.
 */
/**
 * Offered to an admin who isn't a super admin yet.
 *
 * The claim button used to live only on the "not available" screen, which is
 * the one screen an admin never sees — so the moment somebody had portal
 * access there was no route to the tier above it except a shell script. The
 * server decides whether it is allowed (it refuses once anyone holds super
 * admin); this only has to offer it.
 */
/**
 * A held grant, shown inside the portal.
 *
 * The app carries a banner on every screen, but the portal does not — and with
 * no expiry a grant now lasts until somebody releases it. Without this, the
 * one place you would go to check "am I still in someone's account?" is the
 * one place that could not tell you.
 */
function HeldGrant() {
  const { acting, actingName, refreshClaims } = useAuth()
  const [releasing, setReleasing] = useState(false)

  if (!acting) return null

  const name = actingName ?? sessionStorage.getItem('encore.actingAs')

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '16px 24px 0',
        padding: '10px 14px',
        borderRadius: 12,
        background: 'var(--accent)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      <span style={{ minWidth: 0 }}>
        {name ? `Acting as ${name}` : 'Acting as another account'}
      </span>
      <button
        type="button"
        disabled={releasing}
        onClick={async () => {
          setReleasing(true)
          try {
            await adminStopActingAs({})
            sessionStorage.removeItem('encore.actingAs')
            await refreshClaims()
            window.location.reload()
          } catch {
            setReleasing(false)
          }
        }}
        style={{
          marginLeft: 'auto',
          border: '1px solid rgba(255,255,255,0.55)',
          background: 'transparent',
          color: '#fff',
          borderRadius: 999,
          padding: '4px 12px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {releasing ? 'Leaving…' : 'Leave'}
      </button>
    </div>
  )
}

function ClaimSuperAdmin({ onGranted }: { onGranted: () => void }) {
  return (
    <div
      className="panel panel-pad"
      style={{
        margin: '16px 24px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
      }}
    >
      <div className="stack" style={{ gap: 2, minWidth: 200, flex: 1 }}>
        <strong>You're an admin, not a super admin</strong>
        <span className="t-caption faint">
          Super admin is what grants access to other accounts and builds inside a
          prospect's. Nobody holds it yet.
        </span>
      </div>
      <Bootstrap onGranted={onGranted} />
    </div>
  )
}

function Bootstrap({ onGranted }: { onGranted?: () => void } = {}) {
  const [state, setState] = useState<'idle' | 'working' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)

  if (state === 'done') {
    return (
      <p className="t-caption" style={{ color: 'var(--positive)' }}>
        Granted — admin and super admin.
      </p>
    )
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <button
        className="btn btn-quiet"
        style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
        disabled={state === 'working'}
        onClick={async () => {
          setState('working')
          setError(null)
          try {
            await adminBootstrap({})
            setState('done')
            // The claim is on the server; the browser is still holding the
            // token it had before. Forcing a refresh here is the difference
            // between the page updating and the button appearing to do
            // nothing until you sign out and back in.
            onGranted?.()
          } catch (caught) {
            setError((caught as Error).message)
            setState('idle')
          }
        }}
      >
        {state === 'working' ? 'Checking…' : 'Claim super admin'}
      </button>
      {error && (
        <span className="t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </span>
      )}
    </div>
  )
}

function DesktopNav({ email, entries }: { email: string; entries: NavEntry[] }) {
  return (
    <aside
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: '24px 0 18px',
        background: 'var(--panel)',
        borderRight: 'var(--hairline-width) solid var(--hairline)',
      }}
    >
      <div className="stack" style={{ padding: '0 20px', gap: 6 }}>
        <Wordmark height={17} />
        {/* Says plainly which surface you're on. Confusing the admin portal
            for the app is the mistake worth designing against. */}
        <span className="chip chip-accent" style={{ alignSelf: 'flex-start' }}>
          Admin
        </span>
      </div>

      <nav className="stack" style={{ gap: 2 }}>
        {entries.map((entry) => (
          <NavLink
            key={entry.to}
            to={entry.to}
            end={entry.end}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 20px',
              fontSize: '0.9rem',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              background: isActive ? 'var(--panel-raised)' : 'transparent',
              boxShadow: isActive ? 'inset var(--signal-rule) 0 0 0 var(--accent)' : 'none',
            })}
          >
            {({ isActive }) => (
              <>
                <Icon
                  name={entry.icon}
                  strokeWidth={isActive ? 2 : 1.6}
                  style={{ color: isActive ? 'var(--accent-text)' : 'inherit' }}
                />
                <span>{entry.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      <div className="stack" style={{ padding: '0 20px', gap: 8 }}>
        <span className="t-caption truncate" title={email}>
          {email}
        </span>
        <a className="btn btn-quiet" href="/app" style={{ paddingLeft: 0 }}>
          <Icon name="chevronLeft" size={14} /> Back to Encore
        </a>
      </div>
    </aside>
  )
}

function MobileNav({ entries }: { entries: NavEntry[] }) {
  return (
    <>
      <header
        className="spread"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          padding: '12px var(--gutter)',
          paddingTop: 'max(12px, env(safe-area-inset-top))',
          background: 'var(--panel)',
          borderBottom: 'var(--hairline-width) solid var(--hairline)',
        }}
      >
        <div className="row" style={{ gap: 8 }}>
          <Wordmark height={16} />
          <span className="chip chip-accent">Admin</span>
        </div>
        <a className="btn btn-quiet" href="/app">
          Exit
        </a>
      </header>

      <nav
        className="row scroll-x"
        style={{
          gap: 6,
          padding: '10px var(--gutter)',
          borderBottom: 'var(--hairline-width) solid var(--hairline)',
        }}
      >
        {entries.map((entry) => (
          <NavLink
            key={entry.to}
            to={entry.to}
            end={entry.end}
            style={({ isActive }) => ({
              flex: 'none',
              padding: '7px 13px',
              borderRadius: 'var(--radius-chip)',
              fontSize: '0.82rem',
              fontWeight: isActive ? 600 : 400,
              whiteSpace: 'nowrap',
              background: 'var(--panel)',
              border: `${isActive ? '2px' : '1px'} solid ${
                isActive ? 'var(--accent)' : 'var(--hairline)'
              }`,
              color: 'var(--text-primary)',
            })}
          >
            {entry.label}
          </NavLink>
        ))}
      </nav>
    </>
  )
}

/** Shared page frame for the admin screens. */
export function AdminPage({
  title,
  lede,
  actions,
  children,
}: {
  title: string
  lede?: string
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  return (
    <div style={{ padding: '28px var(--gutter) 80px', opacity: mounted ? 1 : 0 }}>
      <div className="sections" style={{ maxWidth: 1100, margin: '0 auto', gap: 22 }}>
        <div className="spread" style={{ alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <div className="stack" style={{ gap: 4 }}>
            <h1 className="t-display-l">{title}</h1>
            {lede && <p className="t-caption" style={{ maxWidth: '60ch' }}>{lede}</p>}
          </div>
          {actions}
        </div>
        {children}
      </div>
    </div>
  )
}
